import { Footer } from '@/components/Footer';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export const Disclaimer = () => {
  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[hsl(22,70%,44%)] transition-colors mb-8" data-testid="disclaimer-back-link">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="disclaimer-heading">
          Disclaimer
        </h1>
        <p className="text-sm text-stone-400 mb-10">Last updated: April 2025</p>

        <div className="space-y-8 text-stone-600 text-[15px] leading-relaxed">
          <section className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <p className="font-semibold text-amber-800">
              Please read this disclaimer carefully before using Ayurveda Consult. By accessing or using our services, you acknowledge and agree to the terms set out below.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>1. Not Medical Advice</h2>
            <p>
              The information, assessments, and recommendations provided by Ayurveda Consult are for <strong>educational and informational purposes only</strong>. They are based on traditional Ayurvedic principles interpreted through AI technology and do not constitute medical advice, diagnosis, or treatment. Our services are not a substitute for professional medical advice, diagnosis, or treatment from a qualified healthcare provider.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>2. AI-Generated Content</h2>
            <p>
              Our Ayurvedic assessments are generated using artificial intelligence technology. While the AI system has been designed to provide thoughtful, relevant recommendations based on Ayurvedic principles, AI-generated content may contain inaccuracies, omissions, or general information that may not be appropriate for your specific health situation. Always verify AI-generated recommendations with a qualified health professional before acting upon them.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>3. Service Tiers and Limitations</h2>
            <div className="space-y-4">
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                <h3 className="font-semibold text-[hsl(120,10%,18%)] mb-1">AI Ayurvedic Assessment</h3>
                <p>This is an AI-generated educational report. It is not a comprehensive diagnosis and is not a substitute for professional healthcare advice. Use it as a starting point for understanding Ayurvedic perspectives on your wellness concerns.</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                <h3 className="font-semibold text-[hsl(120,10%,18%)] mb-1">Doctor-Reviewed Assessment</h3>
                <p>This tier includes a non-clinical review by an Ayurvedic practitioner. The review is advisory in nature and does not replace medical advice, clinical examination, or diagnostic testing. The reviewing practitioner does not establish a doctor-patient relationship through this service.</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                <h3 className="font-semibold text-[hsl(120,10%,18%)] mb-1">1-on-1 Ayurvedic Consultation</h3>
                <p>This consultation provides Ayurvedic diet and lifestyle guidance only. No medicines, treatments, or prescriptions are provided. The consulting practitioner offers guidance based on traditional Ayurvedic principles and is not diagnosing diseases, prescribing medicines, or providing clinical treatment.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>4. Role of Practitioners</h2>
            <p>
              Ayurvedic practitioners associated with our platform are qualified to provide guidance within the scope of Ayurvedic wellness practices. Their role is limited to <strong>reviewing, validating, discussing, and guiding</strong>. They are not:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Diagnosing diseases or medical conditions</li>
              <li>Prescribing pharmaceutical medicines</li>
              <li>Providing clinical or surgical treatment</li>
              <li>Replacing your primary healthcare provider</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>5. Herbal Remedies</h2>
            <p>
              Any herbal remedy suggestions included in your assessment are based on traditional Ayurvedic practice and are provided for informational purposes. Herbal products can interact with prescription medications, cause allergic reactions, or be unsuitable for certain health conditions. Always consult a qualified healthcare provider before starting any herbal supplement, especially if you are pregnant, nursing, taking medications, or have a pre-existing medical condition.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>6. Panchkarma Treatments</h2>
            <p>
              Panchkarma center listings and booking facilitation on our platform are provided for convenience. We make reasonable efforts to list reputable centres but do not guarantee the quality, safety, or outcomes of treatments provided by third-party centres. You are responsible for verifying the credentials and suitability of any Panchkarma centre before undergoing treatment.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>7. Emergency Situations</h2>
            <p className="font-semibold text-red-700">
              If you are experiencing a medical emergency, call your local emergency services immediately. Do not rely on this platform for urgent or emergency medical care.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>8. No Guarantees</h2>
            <p>
              We do not guarantee any specific health outcomes from following the recommendations in your Ayurvedic assessment. Individual results vary based on numerous factors including overall health, adherence to recommendations, and biological individuality. Past user testimonials or ratings on our platform reflect individual experiences and are not indicative of guaranteed results.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>9. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by applicable law, Ayurveda Consult, its operators, affiliates, employees, and associated practitioners shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from your use of our services, reliance on our assessments, or actions taken based on our recommendations.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>10. Refund Policy</h2>
            <p>
              All consultation fees are one-time payments. Refund requests are evaluated on a case-by-case basis. Since assessments are digitally delivered and generated upon payment, refunds may not be available once your report has been generated. Please contact <a href="mailto:me@ayurvedaconsult.online" className="text-[hsl(22,70%,44%)] underline">me@ayurvedaconsult.online</a> for refund enquiries.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>11. Changes to This Disclaimer</h2>
            <p>
              We reserve the right to modify this disclaimer at any time. Changes take effect immediately upon posting. Your continued use of the platform after changes are made constitutes acceptance of the revised disclaimer.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-[hsl(120,10%,18%)] mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>12. Contact</h2>
            <p>
              For questions or concerns about this disclaimer, contact us at:
            </p>
            <p className="mt-2">
              <strong>Ayurveda Consult / AIAYS Institute</strong><br />
              Email: <a href="mailto:me@ayurvedaconsult.online" className="text-[hsl(22,70%,44%)] underline">me@ayurvedaconsult.online</a><br />
              Website: <a href="https://aiays.org" target="_blank" rel="noopener noreferrer" className="text-[hsl(22,70%,44%)] underline">aiays.org</a>
            </p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Disclaimer;
